/**
 * Avvisi: quello che Otto ha capito da solo.
 *
 * La chat è quello che gli chiedi. Gli avvisi sono quello che ti dice lui
 * senza che tu chieda niente — e nascono da un fatto vero arrivato dal
 * gestionale (un messaggio, una prenotazione, un prezzo cambiato), non da una
 * previsione.
 *
 * Ogni avviso porta con sé **cosa si può fare adesso**: un comando già scritto
 * per la chat, un fornitore da chiamare vicino all'immobile, un pezzo da
 * comprare. Un avviso senza uscite è solo una notifica, e le notifiche si
 * imparano a ignorare.
 */

import { z } from "zod";

export const ALERT_KINDS = [
  "guasto_segnalato",
  "ospite_senza_risposta",
  "checkin_senza_pulizia",
  "task_in_ritardo",
  "notte_orfana",
  "prezzo_sotto_minimo",
  "prezzo_minimo_mancante",
  "finestra_vuota",
  "imposta_soggiorno",
  "cin_mancante",
  "scadenza_pagamento",
] as const;

export type AlertKind = (typeof ALERT_KINDS)[number];

/** Tre livelli e basta: se tutto è urgente, niente è urgente. */
export const ALERT_SEVERITIES = [
  "urgente",
  "attenzione",
  "informativo",
] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const ALERT_STATI = ["nuovo", "visto", "risolto", "ignorato"] as const;
export type AlertStato = (typeof ALERT_STATI)[number];

/**
 * Una cosa che si può fare in risposta a un avviso.
 *
 * `chat` passa la palla a Otto con il comando già scritto — e quindi passa
 * dall'approvazione come qualsiasi altra modifica. `link` porta fuori (una
 * ricerca di fornitori, un acquisto): fuori da Otto non ci sono guardrail, e
 * infatti lì Otto non fa niente da solo, apre solo la pagina.
 *
 * `fissa_minimo` non passa dalla chat di proposito. Il prezzo minimo è il
 * guardrail che protegge l'host *dal* modello: farlo scrivere al modello, sia
 * pure su richiesta, vorrebbe dire lasciargli allentare la propria rete. Il
 * valore è già deciso qui, il tap lo conferma e basta — ed è un'impostazione
 * di Otto, non una scrittura sul gestionale.
 */
export const AlertActionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("chat"),
    label: z.string(),
    prompt: z.string(),
    primaria: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("link"),
    label: z.string(),
    url: z.string(),
    dettaglio: z.string().optional(),
    primaria: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("apri"),
    label: z.string(),
    view: z.enum(["inbox", "listings"]),
    id: z.string().optional(),
    primaria: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("fissa_minimo"),
    label: z.string(),
    listingId: z.string(),
    centesimi: z.number().int().min(0),
    primaria: z.boolean().optional(),
  }),
  /**
   * Lo stesso, ma su tante case in un tap.
   *
   * Su un portafoglio grande il prezzo minimo manca su tutte, e un avviso per
   * immobile voleva dire cento righe identiche: la prima impressione del
   * prodotto diventava un muro da scorrere, e un elenco così si impara a
   * ignorare. Ogni casa porta comunque **il suo** numero, calcolato sul suo
   * storico: si raggruppa la domanda, non la risposta.
   */
  /**
   * Una risposta all'ospite già scritta, da approvare lì nell'avviso.
   *
   * L'avviso porta la proposta vera (`actionId`), non un testo da copiare:
   * il tap passa dall'approvazione e dai guardrail come ogni altra scrittura.
   */
  z.object({
    kind: z.literal("bozza"),
    label: z.string(),
    actionId: z.string(),
    primaria: z.boolean().optional(),
  }),
  /** Una scadenza pagata: si segna su Otto, non sul gestionale. */
  z.object({
    kind: z.literal("segna_pagata"),
    label: z.string(),
    scadenzaId: z.string(),
    primaria: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("fissa_minimi"),
    label: z.string(),
    immobili: z
      .array(
        z.object({
          listingId: z.string(),
          nickname: z.string(),
          centesimi: z.number().int().min(0),
        }),
      )
      .min(1),
    primaria: z.boolean().optional(),
  }),
]);

export type AlertAction = z.infer<typeof AlertActionSchema>;

export interface AlertView {
  id: string;
  kind: AlertKind;
  severity: AlertSeverity;
  titolo: string;
  testo: string;
  listingId: string | null;
  listingNickname: string | null;
  conversationId: string | null;
  guestName: string | null;
  azioni: AlertAction[];
  stato: AlertStato;
  createdAt: string;
}

/**
 * Le tre aree in cui un property manager divide i suoi problemi.
 *
 * Tre, non sette: le categorie servono a decidere *cosa guardare adesso*, e
 * sopra le tre-quattro voci si smette di scegliere e si guarda tutto — che è
 * come non averle.
 */
export const ALERT_AREE = ["ospiti", "casa", "ricavi"] as const;
export type AlertArea = (typeof ALERT_AREE)[number];

const AREA_PER_KIND: Record<AlertKind, AlertArea> = {
  guasto_segnalato: "casa",
  checkin_senza_pulizia: "casa",
  task_in_ritardo: "casa",
  ospite_senza_risposta: "ospiti",
  notte_orfana: "ricavi",
  prezzo_sotto_minimo: "ricavi",
  prezzo_minimo_mancante: "ricavi",
  finestra_vuota: "ricavi",
  // Non è un ricavo: sono soldi che hai incassato e che non sono tuoi. Sta in
  // «Casa» perché è un obbligo della casa, come una bolletta.
  imposta_soggiorno: "casa",
  // Stessa ragione: è un obbligo della casa, non un problema di prezzo.
  cin_mancante: "casa",
  // Una rata del condominio, una bolletta: obblighi della casa.
  scadenza_pagamento: "casa",
};

export function areaDi(kind: AlertKind): AlertArea {
  return AREA_PER_KIND[kind];
}

export const AREA_ETICHETTE: Record<
  AlertArea,
  { label: string; spiega: string }
> = {
  ospiti: { label: "Ospiti", spiega: "Chi aspetta una risposta" },
  casa: { label: "Casa", spiega: "Guasti, pulizie, interventi" },
  ricavi: { label: "Ricavi", spiega: "Prezzi e notti da riempire" },
};

/** Etichette leggibili, in un posto solo: le usano interfaccia e prompt. */
export const ALERT_ETICHETTE: Record<AlertKind, string> = {
  guasto_segnalato: "Guasto",
  ospite_senza_risposta: "Ospite in attesa",
  checkin_senza_pulizia: "Pulizia mancante",
  task_in_ritardo: "Task in ritardo",
  notte_orfana: "Notte isolata",
  prezzo_sotto_minimo: "Prezzo sotto il minimo",
  prezzo_minimo_mancante: "Nessun prezzo minimo",
  finestra_vuota: "Settimane vuote",
  imposta_soggiorno: "Imposta di soggiorno",
  cin_mancante: "CIN mancante",
  scadenza_pagamento: "Pagamento in scadenza",
};
